import type { CityConfig } from "../city-template/types";

export const raymondConfig: CityConfig = {
  city: "Raymond",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "raymond",
  route: "/raymond",
  title: "Emergency Passport Raymond, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Raymond, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Rockingham County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Raymond, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Raymond and central Rockingham County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Raymond%20New%20Hampshire%20small%20town%20rural%20area%20with%20classic%20New%20England%20countryside%2C%20rolling%20green%20fields%20and%20forested%20hills%2C%20quiet%20country%20road%20with%20stone%20walls%20and%20maple%20trees%2C%20warm%20summer%20afternoon%20with%20golden%20light%2C%20peaceful%20rural%20scene%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20natural%20green%20and%20warm%20earth%20tones%2C%20photorealistic%20rural%20setting&width=1800&height=700&seq=raymond-nh-hero-001&orientation=landscape",
  heroImageAlt: "Raymond New Hampshire small town rural countryside with rolling green fields and classic New England stone walls",
  geoPosition: "43.0362;-71.1834",
  geoPlacename: "Raymond, New Hampshire",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "10 Causeway St Suite 247, Boston, MA 02222",
    distance: "Located in downtown Boston — approximately 55–70 minutes from Raymond via NH-101 and I-93",
  },
  nearbyCitiesZips: [
    { name: "Raymond", zips: "03077" },
    { name: "Epping", zips: "03042" },
    { name: "Derry", zips: "03038" },
    { name: "Nottingham", zips: "03290" },
    { name: "Candia", zips: "03034" },
    { name: "Chester", zips: "03036" },
    { name: "Deerfield", zips: "03037" },
    { name: "Auburn", zips: "03032" },
    { name: "Sandown", zips: "03873" },
    { name: "Danville", zips: "03819" },
    { name: "Fremont", zips: "03044" },
    { name: "Northwood", zips: "03261" },
    { name: "Barrington", zips: "03825" },
    { name: "Lee", zips: "03861" },
    { name: "Hampstead", zips: "03841" },
    { name: "Kingston", zips: "03848" },
    { name: "Newton", zips: "03858" },
    { name: "Brentwood", zips: "03833" },
    { name: "East Kingston", zips: "03827" },
    { name: "South Hampton", zips: "03882" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Rockingham County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/manchester" },
    { name: "Nashua", route: "/nashua" },
    { name: "Derry", route: "/derry" },
    { name: "Epping", route: "/epping" },
    { name: "Exeter", route: "/exeter" },
    { name: "Portsmouth", route: "/portsmouth" },
    { name: "Londonderry", route: "/londonderry" },
    { name: "Hooksett", route: "/hooksett" },
  ],
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeNote: "Fast Passport Center serves Raymond remotely via secure courier. We do not have a walk-in office in Raymond — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Rockingham County and southern New Hampshire travelers.",
  fpcOfficeDistance: "Serving Rockingham County and central New Hampshire",
  serviceAreaIntro: "From the Main Street area to the Onway Lake region, from the Route 101 corridor to the Nottingham border — Fast Passport Center serves the entire Raymond community. We regularly help travelers from Epping, Derry, Nottingham, and Candia get their passports fast.",
  nearbyCitiesIntro: "We serve all Raymond neighborhoods and nearby Rockingham County cities including Epping, Derry, Nottingham, Candia, Chester, Deerfield, Auburn, Sandown, Danville, Fremont, Northwood, Barrington, Lee, Hampstead, and Kingston. If you are in the greater Raymond area, we have you covered.",
  howItWorksIntro: "Most Raymond travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Manchester-Boston Regional Airport (MHT) just 30 minutes away and Portsmouth International nearby, Rockingham County travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Raymond and Rockingham County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Raymond? Here are the most common ones we hear from Rockingham County travelers.",
  statePageRoute: "/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Raymond international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Karen S.",
      location: "Raymond, NH",
      rating: 5,
      text: "My family trip to Jamaica was in 5 days and my daughter's passport was expired. Fast Passport Center got her a new one in 48 hours. Absolutely saved our vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Michael D.",
      location: "Epping, NH",
      rating: 5,
      text: "Needed to renew for a business trip to Germany. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for Rockingham County.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah J.",
      location: "Nottingham, NH",
      rating: 5,
      text: "Lost my passport the week before my trip to Costa Rica. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "James R.",
      location: "Derry, NH",
      rating: 5,
      text: "As a frequent traveler from the Raymond area, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "raymond-nh-1",
      name: "Raymond Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 Main St",
      city: "Raymond",
      state: "NH",
      zip: "03077",
      phone: "(603) 895-2221",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Raymond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Raymond. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "raymond-nh-2",
      name: "Epping Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 Pleasant St",
      city: "Epping",
      state: "NH",
      zip: "03042",
      phone: "(603) 679-2221",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.5 miles from Raymond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular alternative for eastern Raymond residents. Extended hours available. Photo services on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "raymond-nh-3",
      name: "Derry Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 W Broadway",
      city: "Derry",
      state: "NH",
      zip: "03038",
      phone: "(603) 432-1114",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7.2 miles from Raymond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for southern Rockingham County. Extended hours available. Photo services on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "raymond-nh-4",
      name: "Candia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3 Elm St",
      city: "Candia",
      state: "NH",
      zip: "03034",
      phone: "(603) 483-2221",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "5.8 miles from Raymond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Raymond residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "raymond-nh-5",
      name: "Chester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12 Chester St",
      city: "Chester",
      state: "NH",
      zip: "03036",
      phone: "(603) 887-2221",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "6.3 miles from Raymond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves western Raymond and Chester area. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "raymond-nh-6",
      name: "Auburn Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 Hooksett Rd",
      city: "Auburn",
      state: "NH",
      zip: "03032",
      phone: "(603) 483-2221",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "8.1 miles from Raymond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Raymond residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Raymond photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rockingham County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Raymond applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Raymond families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Raymond travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Raymond or Epping." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Raymond address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Raymond before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Raymond for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Rockingham County travelers." },
    { q: "How fast can I renew my passport in Raymond?", a: "Fast Passport Center offers fast passport renewal in Raymond in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Raymond in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Raymond?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Raymond residents." },
    { q: "Can I expedite a child passport in Raymond?", a: "Yes. Fast Passport Center expedites child passports in Raymond for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Raymond with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Raymond?", a: "With Fast Passport Center's emergency courier service, Raymond travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Raymond?", a: "Popular options include the Raymond Post Office (03077), Epping Post Office (03042), Derry Post Office (03038), and Candia Post Office (03034). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Raymond?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Epping or Nottingham?", a: "We serve all of Rockingham County including Epping, Nottingham, Candia, Chester, Deerfield, Auburn, Sandown, Danville, Fremont, Northwood, Barrington, Lee, Hampstead, and Kingston. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Raymond?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Epping", address: "15 Pleasant St, Epping, NH 03042", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Derry", address: "18 E Broadway, Derry, NH 03038", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Manchester", address: "77 Fort Eddy Rd, Concord, NH 03301", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Derry", address: "20 W Broadway, Derry, NH 03038", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Manchester", address: "344 Loudon Rd, Manchester, NH 03103", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Epping", address: "15 Pleasant St, Epping, NH 03042", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Derry", address: "20 W Broadway, Derry, NH 03038", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Manchester", address: "71 Interstate Dr, Manchester, NH 03103", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Manchester", address: "23 Hazard Ave, Manchester, NH 03109", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Nashua", address: "23 Hazard Ave, Nashua, NH 03109", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Derry", address: "1 Derry Rd, Derry, NH 03038", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Concord", address: "23 Hazen Dr, Concord, NH 03301", hours: "Mon–Fri: 8AM–5PM", note: "Convenient for central Rockingham County residents.", featured: true },
  ],
  airports: [
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "30 miles from Raymond", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major commercial airport for Raymond travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: true },
    { code: "BOS", name: "Boston Logan International Airport", distance: "65 miles from Raymond", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-93.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
    { code: "PSM", name: "Portsmouth International Airport", distance: "35 miles from Raymond", address: "36 Airport Rd, Portsmouth, NH 03801", note: "Regional airport with limited commercial service. Useful for some domestic routes.", flightUrl: "https://www.flyportsmouth.com/", flightLabel: "PSM Flight Info", featured: false },
  ],
};